import type { CoworkWorkspaceAgent } from "./api"

export function selectDefaultCoworkAgent(
  agents: CoworkWorkspaceAgent[],
  defaultAgentID: string | undefined,
  scope: { companyID: string; projectID: string },
) {
  const available = agents.filter(
    (agent) =>
      agent.status === "active" &&
      agent.assignments.some(
        (assignment) =>
          assignment.scope_type === "workspace" ||
          (assignment.scope_type === "entity" && assignment.scope_id === scope.companyID) ||
          (assignment.scope_type === "project" && assignment.scope_id === scope.projectID),
      ),
  )
  return (
    available.find((agent) => agent.id === defaultAgentID) ??
    available.find((agent) => agent.template_id === defaultAgentID) ??
    available[0]
  )
}

export function selectAssignedCoworkAgentByTemplate(
  agents: CoworkWorkspaceAgent[],
  templateID: string,
  scope: { companyID: string; projectID: string },
  preferredAgentID?: string,
) {
  const available = agents.filter(
    (agent) =>
      agent.template_id === templateID &&
      agent.status === "active" &&
      agent.assignments.some(
        (assignment) =>
          assignment.scope_type === "workspace" ||
          (assignment.scope_type === "entity" && assignment.scope_id === scope.companyID) ||
          (assignment.scope_type === "project" && assignment.scope_id === scope.projectID),
      ),
  )
  const rank = (agent: CoworkWorkspaceAgent) => {
    if (agent.assignments.some((assignment) => assignment.scope_type === "project" && assignment.scope_id === scope.projectID)) return 0
    if (agent.assignments.some((assignment) => assignment.scope_type === "entity" && assignment.scope_id === scope.companyID)) return 1
    return 2
  }
  return available.find((agent) => agent.id === preferredAgentID) ??
    available.toSorted((left, right) => rank(left) - rank(right) || left.id.localeCompare(right.id))[0]
}

type DefaultAgentOption = { id: string; name: string; templateID: string }

export function workspaceDefaultAgentOptions(
  agents: CoworkWorkspaceAgent[],
  builtIn: DefaultAgentOption[],
): DefaultAgentOption[] {
  const seen = new Set<string>()
  const assigned = agents
    .filter((agent) => agent.status === "active" && agent.assignments.some((item) => item.scope_type === "workspace"))
    .filter((agent) => {
      const key = `${agent.template_id}:${agent.name.trim().toLowerCase()}`
      if (seen.has(key)) return false
      seen.add(key)
      return true
    })
    .map((agent) => ({ id: agent.id, name: agent.name, templateID: agent.template_id ?? "" }))
  return [...builtIn.filter((item) => !assigned.some((agent) => agent.templateID === item.id)), ...assigned]
}

export function visibleWorkspaceDefaultAgentID(
  defaultID: string | null | undefined,
  agents: CoworkWorkspaceAgent[],
  options: DefaultAgentOption[],
): string {
  if (options.some((option) => option.id === defaultID)) return defaultID!
  const persisted = agents.find((agent) => agent.id === defaultID)
  const representative = persisted && options.find((option) =>
    option.templateID === persisted.template_id && option.name.trim().toLowerCase() === persisted.name.trim().toLowerCase(),
  )
  return representative?.id ?? options.find((option) => option.templateID === defaultID)?.id ??
    options.find((option) => option.templateID === "accounting")?.id ?? options[0]?.id ?? "accounting"
}
